/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../../router/paths';
import {
  X,
  Copy,
  Check,
  ExternalLink,
  Clock,
  Globe,
  AlertCircle,
  Calendar,
  Gauge,
  Layers,
  FileText,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import {
  OperationsSession,
  STATUS_COLORS
} from '@utils/types/back-office/operations';
import { useState } from 'react';
import toast from 'react-hot-toast';

/* eslint-disable no-restricted-syntax -- Semantic session-status colors */
import {
  getErrorCategoryLabel,
  getErrorCategoryStyle
} from '@utils/types/errors/error-categories';

interface SessionDetailDrawerProps {
  session: OperationsSession | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function SessionDetailDrawer({
  session,
  isOpen,
  onClose
}: SessionDetailDrawerProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    toast.success(t('copied-to-clipboard'));
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '-';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

    return parts.join(' ');
  };

  const handleViewCustomer = () => {
    if (session) {
      navigate(PATHS(String(session.organization_id)).CUSTOMER_CONTENT);
    }
  };

  if (!isOpen || !session) return null;

  const statusColors =
    STATUS_COLORS[session.status] || STATUS_COLORS['processing'];

  const bgColor = getColour('baseLayer') || '#ffffff';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className="absolute right-0 top-0 h-full w-full max-w-md shadow-2xl overflow-y-auto"
        style={{ backgroundColor: bgColor }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between p-4 border-b"
          style={{
            backgroundColor: getColour('baseLayer'),
            borderColor: getColour('border')
          }}
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('session-details')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-2xl hover:bg-opacity-10"
            style={{ color: getColour('inactiveText') }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Status badge with flags */}
          <div
            className="rounded-2xl p-4"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center justify-between">
              <span
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('status')}
              </span>
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: statusColors.bg,
                    color: statusColors.text
                  }}
                >
                  {session.error_code === 'bad_file'
                    ? 'BAD FILE'
                    : t(session.status)}
                </span>
                {session.is_stuck && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: 'rgba(249, 115, 22, 0.15)',
                      color: '#F97316'
                    }}
                  >
                    <Clock className="h-3 w-3" />
                    {t('stuck')}
                  </span>
                )}
                {session.is_stranded && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.15)',
                      color: '#EF4444'
                    }}
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {t('stranded')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Session ID */}
          <div
            className="rounded-2xl p-4"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center justify-between mb-1">
              <span
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('session-id')}
              </span>
              <button
                onClick={() => handleCopy(session.session_id, 'session_id')}
                className="p-1 rounded-2xl hover:bg-opacity-10"
                style={{ color: getColour('inactiveText') }}
              >
                {copiedField === 'session_id' ? (
                  <Check className="h-4 w-4" style={{ color: '#22C55E' }} />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
            <p
              className="font-mono text-sm break-all"
              style={{ color: getColour('normalText') }}
            >
              {session.session_id}
            </p>
          </div>

          {/* Filename */}
          {session.filename && (
            <div
              className="rounded-2xl p-4"
              style={{ backgroundColor: getColour('secondLayer') }}
            >
              <div className="flex items-center gap-2 mb-1">
                <FileText
                  className="h-3.5 w-3.5"
                  style={{ color: getColour('inactiveText') }}
                />
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('filename')}
                </span>
              </div>
              <p
                className="text-sm break-all"
                style={{ color: getColour('normalText') }}
              >
                {session.filename}
              </p>
            </div>
          )}

          {/* Organization */}
          <div
            className="rounded-2xl p-4"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center justify-between mb-1">
              <span
                className="text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {t('organization')}
              </span>
              <button
                onClick={handleViewCustomer}
                className="flex items-center gap-1 text-xs hover:underline"
                style={{ color: getColour('activeText') }}
              >
                {t('view')}
                <ExternalLink className="h-3 w-3" />
              </button>
            </div>
            <p
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {session.organization_name}
            </p>
            <p className="text-xs" style={{ color: getColour('inactiveText') }}>
              ID: {session.organization_id}
            </p>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Language */}
            <div
              className="rounded-2xl p-3"
              style={{ backgroundColor: getColour('secondLayer') }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Globe
                  className="h-3.5 w-3.5"
                  style={{ color: getColour('inactiveText') }}
                />
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('language')}
                </span>
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: getColour('normalText') }}
              >
                {session.language}
              </p>
            </div>

            {/* Duration */}
            <div
              className="rounded-2xl p-3"
              style={{ backgroundColor: getColour('secondLayer') }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock
                  className="h-3.5 w-3.5"
                  style={{ color: getColour('inactiveText') }}
                />
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('duration')}
                </span>
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: getColour('normalText') }}
              >
                {formatDuration(session.duration)}
              </p>
            </div>

            {/* Mode */}
            <div
              className="rounded-2xl p-3"
              style={{ backgroundColor: getColour('secondLayer') }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Layers
                  className="h-3.5 w-3.5"
                  style={{ color: getColour('inactiveText') }}
                />
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('mode')}
                </span>
              </div>
              <p
                className="text-sm font-medium capitalize"
                style={{ color: getColour('normalText') }}
              >
                {session.session_mode}
              </p>
            </div>

            {/* Priority */}
            <div
              className="rounded-2xl p-3"
              style={{ backgroundColor: getColour('secondLayer') }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Gauge
                  className="h-3.5 w-3.5"
                  style={{ color: getColour('inactiveText') }}
                />
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('priority')}
                </span>
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: getColour('normalText') }}
              >
                {session.priority ?? '-'}
              </p>
            </div>
          </div>

          {/* Queue Status and Retry Count */}
          {(session.queue_status || session.retry_count !== null) && (
            <div
              className="rounded-2xl p-4"
              style={{ backgroundColor: getColour('secondLayer') }}
            >
              <h3
                className="text-xs font-medium mb-3"
                style={{ color: getColour('inactiveText') }}
              >
                {t('queue-info')}
              </h3>
              <div className="space-y-2">
                {session.queue_status && (
                  <div className="flex justify-between">
                    <span
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('queue-status')}
                    </span>
                    <span
                      className="text-xs font-medium capitalize"
                      style={{ color: getColour('normalText') }}
                    >
                      {session.queue_status}
                    </span>
                  </div>
                )}
                {session.retry_count !== null && (
                  <div className="flex justify-between items-center">
                    <span
                      className="text-xs flex items-center gap-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      <RotateCcw className="h-3 w-3" />
                      {t('retry-count')}
                    </span>
                    <span
                      className="text-xs font-medium"
                      style={{
                        color:
                          session.retry_count > 0
                            ? '#F97316'
                            : getColour('normalText')
                      }}
                    >
                      {session.retry_count}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div
            className="rounded-2xl p-4"
            style={{ backgroundColor: getColour('secondLayer') }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Calendar
                className="h-3.5 w-3.5"
                style={{ color: getColour('inactiveText') }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                {t('timestamps')}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('started')}
                </span>
                <span
                  className="text-xs"
                  style={{ color: getColour('normalText') }}
                >
                  {formatDateTime(session.date_session_start)}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('last-updated')}
                </span>
                <span
                  className="text-xs"
                  style={{ color: getColour('normalText') }}
                >
                  {formatDateTime(session.last_update)}
                </span>
              </div>
            </div>
          </div>

          {/* Error message */}
          {session.error_message && (
            <div
              className="rounded-2xl p-4 border"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                borderColor: session.error_category
                  ? getErrorCategoryStyle(session.error_category).color + '33'
                  : 'rgba(239, 68, 68, 0.2)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <AlertCircle
                    className="h-4 w-4"
                    style={{ color: '#EF4444' }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: '#EF4444' }}
                  >
                    {t('error-message')}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {session.error_category && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold uppercase"
                      style={{
                        backgroundColor: getErrorCategoryStyle(
                          session.error_category
                        ).bg,
                        color: getErrorCategoryStyle(session.error_category)
                          .color
                      }}
                    >
                      {getErrorCategoryLabel(session.error_category)}
                    </span>
                  )}
                  {session.error_code === 'bad_file' &&
                    session.error_reason && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: 'rgba(234, 88, 12, 0.1)',
                          color: '#EA580C'
                        }}
                      >
                        {session.error_reason.replace(/_/g, ' ')}
                      </span>
                    )}
                </div>
              </div>
              <pre
                className="text-xs font-mono whitespace-pre-wrap overflow-x-auto p-2 rounded-2xl"
                style={{
                  backgroundColor: getColour('baseLayer'),
                  color: getColour('normalText')
                }}
              >
                {session.error_message}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
